import {
  AddressBooks,
  AddressBooksEntries,
  AgentStateResponse,
  SkillEvent,
  DirectoryResponse,
  DirectoryEntities,
} from '@nice-ccf/common-sdk';

/**
 * Directory Adapter class to handle the agent api's data response
 */
export class CXoneDirectoryAdapter {
  /**
   * Used to handle the directory response and return the combined directory model object
   * @param response - the directory api response - state,skill and address book
   * @example -
   * ```
   * const directoryAdapter = new CXoneDirectoryAdapter();
   * this.directoryAdapter.handleEvent(data);
   * ```
   */
  public async handleEvent(response: any) {
    const agentStateList = [] as Array<AgentStateResponse>;
    const SkillList = [] as Array<SkillEvent>;
    const addressBookList = [] as Array<AddressBooks>;
    let addressBookEntriesCount = 0;
    if (response.get(DirectoryEntities.AGENT_LIST)) {
      const agentStateValue = response.get(DirectoryEntities.AGENT_LIST);
      if (agentStateValue.status === 'fulfilled' && agentStateValue?.value.resultSet?.agentState) {
        agentStateValue.value.resultSet.agentState.forEach((state: { [key: string]: string }) => {
          const agentList = new AgentStateResponse();
          agentList.parse(state);
          agentStateList.push(agentList);
        });
      }
    }
    if (response.get(DirectoryEntities.SKILL_LIST)) {
      const skillValue = response.get(DirectoryEntities.SKILL_LIST);
      if (skillValue.status === 'fulfilled' && skillValue?.value.resultSet?.Skills) {
        skillValue.value.resultSet.Skills.forEach((skill: { [key: string]: string }) => {
          const skillEvent = new SkillEvent();
          skillEvent.parse(skill);
          SkillList.push(skillEvent);
        });
      }
    }
    if (response.get(DirectoryEntities.ADDRESS_BOOK_LIST)) {
      const addressBookValue = response.get(DirectoryEntities.ADDRESS_BOOK_LIST);
      if (addressBookValue?.status === 'fulfilled' && addressBookValue?.value.addressBooks) {
        addressBookValue.value.addressBooks.forEach((addressBook: any) => {
          const addressBooks: AddressBooks = {
            addressBookName: addressBook.addressBookName,
            addressBookId: addressBook.addressBookId,
            addressBookType: addressBook.addressBookType,
          };
          if (addressBook?.addressBookEntries?.length) {
            addressBookEntriesCount = addressBookEntriesCount + addressBook.addressBookEntries.length;
            addressBooks.addressBookEntries = this.formatAddressBookEntries(addressBook?.addressBookEntries);
          }
          addressBookList.push(addressBooks);
        });
      }
    }
    
    const directoryResponse: DirectoryResponse = {
      agentList: { data: agentStateList},
      skillList: { data: SkillList},
      addressBookList: addressBookEntriesCount ? {data: addressBookList} : {data: []},
    };
    return directoryResponse;
  }

  /**
   * This method to formatAddressBookEntries
   * @example -
   * ```
   * formatAddressBookEntries(entries);
   * ```
   * @param entries - address book entries
   * @returns - formatted address book entries
   */
  public formatAddressBookEntries(entries: any[]): Array<AddressBooksEntries> {
    const standardAddressBookEntries: Array<AddressBooksEntries> = [];
    entries.forEach((addressBook: any) => {
      const addressBooksEntries: AddressBooksEntries = {
        firstName: addressBook.firstName,
        lastName: addressBook.lastName,
        phone: addressBook.phone,
        mobile: addressBook.mobile,
        email: addressBook.email,
        addressBookName: addressBook.firstName + ' ' + addressBook.lastName,
        addressBookEntryId: addressBook.addressBookEntryId,
        externalId: addressBook?.externalId,
        externalState: addressBook?.externalState,
        company: addressBook?.company,
        stateId: addressBook?.stateId,
        isDeleted: addressBook?.isDeleted,
      };
      standardAddressBookEntries.push(addressBooksEntries);
    });
    return standardAddressBookEntries;
  }
}
